// App-wide SPA routing for links inside server components (islands).
//
// Island HTML is not hydrated, so <a> links rendered by NuxtLink inside an island have
// no vue-router click handler — a click would trigger a full page reload. Nuxt marks
// every island root with `data-island-uid`, so a single delegated click listener scoped
// to `[data-island-uid] a` restores client-side navigation for ALL islands at once,
// without hydrating any of them or shipping their data back into the payload.
//
// This is the maintainer-acknowledged "pretend client-side routing" approach; use the
// selectiveClient / nuxt-client directive only for islands that need genuine per-widget
// interactivity beyond links.
export default defineNuxtPlugin((nuxtApp) => {
    function onClick(event: MouseEvent) {
        // respect modifier keys / non-left clicks (open in new tab etc.)
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return
        }

        const anchor = (event.target as HTMLElement)?.closest?.('a')
        if (!(anchor instanceof HTMLAnchorElement)) return

        // Match both an <a> nested inside an island AND an <a> that is itself the island
        // root (single-node link component) — closest() includes the element itself.
        if (!anchor.closest('[data-island-uid]')) return

        const href = anchor.getAttribute('href')
        if (!href || anchor.target === '_blank' || anchor.hasAttribute('download')) return

        // only intercept same-origin internal links
        const url = new URL(href, window.location.origin)
        if (url.origin !== window.location.origin) return

        event.preventDefault()
        navigateTo(url.pathname + url.search + url.hash)
    }

    nuxtApp.hook('app:mounted', () => {
        document.addEventListener('click', onClick)
    })
})
